import api from './api';

const navService = {
  /**
   * Get NAV configuration and current status
   * @param {object} options - { reportingCurrency }
   */
  async getConfig(options = {}) {
    const response = await api.get('/nav/config', { params: options });
    return response.data;
  },

  /**
   * Item 33: update NAV configuration (currently: change base currency).
   * @param {object} params - { init_currency: 'USD' | 'AUD' | ... }
   * @returns {Promise<{ success, data: { config, recalc } }>}
   */
  async updateConfig(params) {
    const response = await api.patch('/nav/config', params);
    return response.data?.data || response.data;
  },

  /** The start day (1 Oct 2026): per account, is the one screen needed and what to pre-fill. */
  async assessStartDay() {
    const response = await api.get('/nav/start-day');
    return response.data?.data || response.data;
  },

  /** Where Kodahfy's open trades and the broker's holdings disagree. */
  async brokerCheck(accountId = null) {
    const response = await api.get('/nav/broker-check', { params: accountId ? { accountId } : {} });
    return response.data?.data || response.data;
  },

  /**
   * Get current NAV calculations
   * @param {object} options - { reportingCurrency }
   */
  async getCurrent(options = {}) {
    const response = await api.get('/nav/current', { params: options });
    return response.data;
  },

  /**
   * Get current NAV using snapshot-based (line-item) approach
   * This matches the values shown in NAV snapshots table on the NAV page
   * Use this for Dashboard FUM to ensure consistency
   * @param {object} options - { reportingCurrency }
   */
  async getCurrentFromSnapshots(options = {}) {
    const response = await api.get('/nav/current-snapshot', { params: options });
    return response.data;
  },

  /**
   * Get NAV snapshots history
   * @param {object} options - { limit, order }
   */
  async getSnapshots(options = {}) {
    const response = await api.get('/nav/snapshots', { params: options });
    return response.data;
  },

  /**
   * Get NAV snapshots with accurate Period P&L in reporting currency
   * Each underlying transaction is converted at its own date rate
   * @param {object} options - { limit, reportingCurrency }
   */
  async getSnapshotsWithReportingPnl(options = {}) {
    const response = await api.get('/nav/snapshots-reporting', { params: options });
    return response.data;
  },

  /**
   * Create manual snapshot
   * @param {string} snapshotDate - Optional date for snapshot
   */
  async createSnapshot(snapshotDate = null) {
    const response = await api.post('/nav/snapshots', { snapshotDate });
    return response.data;
  },

  /**
   * Get unit transactions history
   * @param {object} options - { reportingCurrency }
   */
  async getUnitTransactions(options = {}) {
    const response = await api.get('/nav/unit-transactions', { params: options });
    return response.data;
  },

  /**
   * Get system attribution summary
   */
  async getSystemAttribution() {
    const response = await api.get('/nav/system-attribution');
    return response.data;
  },

  /**
   * Export NAV data
   */
  async exportData(reportingCurrency) {
    const response = await api.get('/nav/export', { params: reportingCurrency ? { reportingCurrency } : {} });
    return response.data;
  },

  /**
   * Reset NAV tracking
   */
  async reset() {
    const response = await api.post('/nav/reset');
    return response.data;
  },

  /**
   * Backfill missing unit transactions
   */
  async backfill() {
    const response = await api.post('/nav/backfill');
    return response.data;
  },

  /**
   * Get debug breakdown
   */
  async getDebug() {
    const response = await api.get('/nav/debug');
    return response.data;
  },

  /**
   * Get drill-down data for a line-item column in a period
   * @param {object} options - { periodStart, periodEnd, column, reportingCurrency }
   */
  async getDrilldown(options = {}) {
    const response = await api.get('/nav/drilldown', { params: options });
    return response.data;
  },

  // ==========================================
  // MANUAL JOURNALS
  // ==========================================

  /**
   * Get manual journals with optional filters
   * @param {object} options - { startDate, endDate, category, system, search, limit, offset }
   */
  async getJournals(options = {}) {
    const response = await api.get('/nav/journals', { params: options });
    return response.data;
  },

  /**
   * Create a new manual journal entry
   * @param {object} data - { journalDate, description, category, amount, allocatedToSystem, notes }
   */
  async createJournal(data) {
    const response = await api.post('/nav/journals', data);
    return response.data;
  },

  /**
   * Update a manual journal entry
   * @param {string} id - Journal ID
   * @param {object} data - Updated fields
   */
  async updateJournal(id, data) {
    const response = await api.put(`/nav/journals/${id}`, data);
    return response.data;
  },

  /**
   * Delete a manual journal entry
   * @param {string} id - Journal ID
   */
  async deleteJournal(id) {
    const response = await api.delete(`/nav/journals/${id}`);
    return response.data;
  },

  // ==========================================
  // ACCOUNT BALANCES
  // ==========================================

  /**
   * Get account balances for reconciliation quick check
   */
  async getAccountBalances() {
    const response = await api.get('/nav/account-balances');
    return response.data;
  },

  // ==========================================
  // TRADING SYSTEMS
  // ==========================================

  /**
   * Get list of trading systems for dropdowns
   */
  async getTradingSystems() {
    const response = await api.get('/nav/trading-systems');
    return response.data;
  },

  // ==========================================
  // SYSTEM NAV
  // ==========================================

  /**
   * Get current system NAV data for all trading systems
   * @param {object} options - { reportingCurrency }
   */
  async getSystemNavData(options = {}) {
    const response = await api.get('/nav/system-nav', { params: options });
    return response.data;
  },

  /**
   * Get NAV history for a specific trading system
   * @param {string} systemName - Trading system name
   * @param {object} options - { reportingCurrency }
   */
  async getSystemNavHistory(systemName, options = {}) {
    const response = await api.get(`/nav/system-nav/${encodeURIComponent(systemName)}`, { params: options });
    return response.data;
  },

  /**
   * Backfill system NAV snapshots for all trading systems
   */
  async backfillSystemNav() {
    const response = await api.post('/nav/system-nav/backfill');
    return response.data;
  },

  /**
   * Convert export data to CSV string
   */
  exportToCsv(data) {
    if (!data || data.length === 0) return '';

    const headers = [
      'Period',
      'Date',
      'Currency',
      'Total NAV',
      'Total Units',
      'NAV Per Unit',
      'Period Return %',
      'Cumulative Return %',
      'Period P&L',
      'Trade P&L',
      'Dividends',
      'Interest',
      'Costs',
      'FX P&L',
      'Capital Additions',
      'Capital Withdrawals'
    ];

    const cell = (v) => (v == null ? '' : v);
    const rows = data.map(row => [
      row.period,
      row.date,
      cell(row.currency),
      row.totalNav,
      row.totalUnits,
      row.navPerUnit,
      row.periodReturnPct,
      row.cumulativeReturnPct,
      row.periodPnl,
      cell(row.tradePnl),
      cell(row.dividends),
      cell(row.interest),
      cell(row.costs),
      cell(row.fxPnl),
      row.capitalAdditions,
      row.capitalWithdrawals
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    return csvContent;
  },

  /**
   * Download CSV file
   */
  downloadCsv(data, filename = 'nav_export.csv') {
    const csvContent = this.exportToCsv(data);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

export default navService;
